import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom";
import { useDispatch } from "react-redux";
import ProjectHeader from "../../_shared/ProjectHeader/ProjectHeader";
import { HeaderContext } from "../../../../HeaderContext";
import { breadCrumbsArray } from "../../../../data/breadCrumbs";
import { setDynamicBreadcrumbs } from "../../_shared/_redux/ProjectActions";
import {
  EXECUTION_BLUEPRINT,
  EXECUTION_BLUEPRINT_REVIEW,
  EXECUTION_BLUEPRINT_WIG_CONNECTIONS,
} from "../../../../data/RouteUrls";
import {
  CheckIcon,
  CloseIcon,
  InfoIcon,
} from "../../StrategyCanvas/StrategyCanvasHelpers";
import WigConnectionsCanvas from "./WigConnectionsCanvas";
import WigConnectionsInfoModal from "./WigConnectionsInfoModal";
// Add / remove a WIG — handoff #25 §2. Shared with stages 1 and 3 and with the
// coach app; see the component's own header.
import DeleteWigDialog from "../ExecutionBlueprintLiveReview/_shared/DeleteWigDialog";
import AddWigDialog from "../ExecutionBlueprintLiveReview/_shared/AddWigDialog";
import { redirectForStep } from "../_redux/liveReviewStepRoutes";
import ConfirmStepModal, { CONFIRM_WIG_CONNECTIONS } from "./ConfirmStepModal";
import liveReviewSocketStore, {
  useLiveReviewSocket,
  LIVE_REVIEW_STEP,
  PARTICIPANT_ROLE,
} from "../ExecutionBlueprintLiveReview/_socket/LiveReviewSocketStore";
import {
  buildConnectorGraph,
  toEndpoint,
} from "../ExecutionBlueprintLiveReview/_shared/connectorsGraph";
import { useExecutionBlueprintStoreSelector } from "../_redux/ExecutionBlueprintStore";
// The shared skeleton chip — one shimmer across every live-review screen.
import EbSkeleton from "../ExecutionBlueprintLiveReview/_shared/EbSkeleton";
import "./WigConnections.scss";

// ─────────────────────────────────────────────────────────────────────────────
// WIG Connections - Figma node 33831:47357. **LIVE as of 2026-08-28.**
//
// This is `live_review_step: "connectors_review"` (handoff §6a) on its own
// screen. It was design-only on a hand-written mock until the §6a contract
// arrived; `WigConnectionsData.js` is now DELETED, which is exactly the trigger
// D13 named. Everything on screen is the server's graph, and every add, change
// and delete is a socket emit echoed back to BOTH parties.
//
// ⚠️ IT IS A STEP, NOT A DESTINATION. The coach does not navigate here and there
// is no link to it: the Live Review screen pushes both parties here when
// `live_review_step_changed` says so, and this screen bounces anyone whose step
// says they shouldn't be here. §6a.4 is the reason it re-joins the socket itself
// rather than trusting the store to be warm — a reload, or an email link after
// the agent finished while the user was away, must land correctly, and "the step
// is what routes your screen, not which broadcast you happened to catch."
//
// ⚠️ The CEO app holds a near-identical copy of this screen. The two people are
// editing ONE graph while talking to each other, so a difference between the two
// screens is a live disagreement. The shared pieces are byte-identical modules
// (`_shared/connectorsGraph.js`, `_socket/LiveReviewSocketStore.js`); only this
// container differs, for app chrome.
//
// Three things the contract settles that are easy to get wrong here:
//
//   - **The overview panel's numbers are the SERVER's.** Every connector
//     broadcast carries a freshly recomputed `overview`; §6a says to read it
//     straight from there. Deriving `total_connections` locally would be a second
//     source of truth for a number two people compare out loud.
//   - **`unreviewed_count` is always equal to `ai_suggested_count`** — a known
//     backend limitation the handoff flags as not-to-build-around. So they are
//     ONE tile here, not two identical ones that would look like our bug.
//   - **An empty graph is a real answer, not an error.** A twice-failed agent
//     still sends the block with `edges: []` and every count 0.
//
// ⚠️ **"Confirm Connections" IS built** (2026-08-29). The line that used to sit
// here said §9 forbade it — handoff v3's §6b superseded that outright ("Confirm
// Connections is now built"). It emits `confirm_connections`, which starts the
// Year 1 shortlist agent and moves both parties on. §6b.1: no completeness gate,
// so it is NOT conditioned on how many connectors exist.
// ─────────────────────────────────────────────────────────────────────────────

// Module scope, per CLAUDE.md: the hook resubscribes on selector identity, so an
// inline arrow would resubscribe on every render — and this store pushes a
// broadcast for every connector either party touches.
const selectSnapshot = (state) => state.snapshot;

// The steps this screen is the right home for. Everything else routes away
// via the shared table in `_redux/liveReviewStepRoutes.js` —
// this screen is the connector map, and only `connectors_review` shows it.
const OWN_STEPS = [LIVE_REVIEW_STEP.CONNECTORS_REVIEW];

const selectJoined = (state) => state.joined;
const selectJoining = (state) => state.joining;
const selectLastError = (state) => state.lastError;
const selectAdvancing = (state) => state.advancing;
const selectAddPending = (state) => state.addPending;

const selectClients = (state) => state.clients;

// One-time explainer flag, same convention as `ebLiveReviewInfoSeen`. Not keyed
// by blueprint id: the dialog teaches a GESTURE, not this blueprint's contents,
// so someone who has learnt it once does not need teaching again on their next
// client. (Per-resource flags in this codebase do include the id — see
// `scReviewInfoSeen` — and this is the deliberate exception.)
const WIG_CONNECTIONS_INFO_SEEN_KEY = "ebWigConnectionsInfoSeen";

// ⚠️ 0.6 → 0.35 WHEN THE GRID WENT TO TEN COLUMNS (2026-09-14). A ten-column
// band is ~2400px wide, and Fit computes `available / naturalSize.w` — about
// 0.49 in a 1200px scroller, and lower on a laptop with the overview panel
// open. Clamped at 0.6 the Fit button would have silently stopped fitting:
// pressing it would leave the right-hand WIGs off screen with no indication
// why.
//
// 0.35 is the floor that makes Fit honest at the widest the map can now be.
// Cards are small at that scale, which is the correct trade — Fit exists to
// show the SHAPE of the graph, and reading a card is what 100% is for.
const ZOOM_MIN = 0.35;
// Capped at 1, not 1.4: 100% is the map's designed size and there is nothing to
// gain by magnifying past it — the cards are already at their intended scale, so
// zooming in only pushes connected WIGs off screen, which is the opposite of
// what this map is for. Zooming OUT is the useful direction and now goes to 35%
// (see `ZOOM_MIN`), since a ten-column graph is far wider than any screen at
// full size.
const ZOOM_MAX = 1;
const ZOOM_STEP = 0.1;
// The canvas's own right padding (20px) plus a little air, so the right-most
// card is not flush against the viewport edge after a fit.
const FIT_GUTTER = 24;

const OverviewStat = ({ value, label, tone }) => (
  <div className={`wc-stat wc-stat--${tone}`}>
    <span className="wc-stat-value">{value}</span>
    <span className="wc-stat-label">{label}</span>
  </div>
);

const MANAGE_ROWS = [
  {
    key: "add",
    glyph: "⤳",
    title: "Add",
    text: "Drag from a WIG point to another WIG.",
  },
  {
    key: "change",
    glyph: "⇄",
    title: "Change",
    text: "Select a line and drag an endpoint.",
  },
  {
    key: "delete",
    glyph: "🗑",
    title: "Delete",
    text: "Select a line and press Delete.",
  },
];

// Full sentences, punctuated — they read as a sequence of instructions, and
// the Add/Change/Delete rows above them are written the same way.
const NEXT_STEPS = [
  "Review the connection map.",
  "Make any needed changes.",
  "Confirm Connections.",
  "Continue to prioritising your 12-month WIGs.",
];

// ── Loading ─────────────────────────────────────────────────────────────────
// A skeleton shaped like the connector map, not a "Connecting…" line (removed
// on instruction). Five band rails — BHAG plus the four WIG sections, which is
// always the shape — each with placeholder cards, so the canvas area is the
// size it will be rather than a line of text in the middle of an empty panel.
// ⚠️ THE CONTAINER NO LONGER CARRIES `wc-skel` (2026-09-03). It used to, and
// that was a real defect rather than a tidiness point: `.wc-skel` was defined
// TWICE in the stylesheet — once as the shimmering chip and once as this
// panel's flex layout — so the wrapper picked up the gradient too and the whole
// canvas area pulsed as one slab *behind* the chips sitting on it. Two sweeps
// at different scales, overlapping. The container is now `.wc-skel-panel` and
// owns only layout.
//
// Each band's card count varies deliberately. The real canvas has uneven rows,
// and five identical rows of three read as a loading graphic where uneven ones
// read as a structure whose contents have not arrived.
const SKELETON_BANDS = [3, 2, 4, 2, 3];

const WigConnectionsSkeleton = () => (
  <div className="wc-skel-panel" aria-hidden="true">
    {SKELETON_BANDS.map((cards, band) => (
      // eslint-disable-next-line react/no-array-index-key
      <div className="wc-skel-band" key={band}>
        <EbSkeleton className="wc-skel-rail" i={band} />
        <div className="wc-skel-cards">
          {Array.from({ length: cards }).map((_, card) => (
            // eslint-disable-next-line react/no-array-index-key
            <EbSkeleton className="wc-skel-card" key={card} i={band + card} />
          ))}
        </div>
      </div>
    ))}
  </div>
);

const WigConnections = () => {
  const history = useHistory();
  const { blueprintId } = useParams();

  // The graph is the socket's, not local state. Nothing here is optimistic:
  // §6 broadcasts every successful mutation back to the actor as well as the
  // other party, so the echo is the single render path and there is no
  // reconciliation to write. A second drag landing before the first echoes is
  // stopped by the STORE's own pending guard — this screen no longer subscribes
  // to it, because the only thing it drove here was a "Saving…" chip that
  // shifted the toolbar (see the note at its old call site).
  const snapshot = useLiveReviewSocket(selectSnapshot);
  const joined = useLiveReviewSocket(selectJoined);
  const joining = useLiveReviewSocket(selectJoining);
  const lastError = useLiveReviewSocket(selectLastError);
  const advancing = useLiveReviewSocket(selectAdvancing);
  const addPending = useLiveReviewSocket(selectAddPending);
  const [zoom, setZoom] = useState(1);
  const [panelOpen, setPanelOpen] = useState(true);
  // The Add/Change/Delete instructions, shut by default — see the note at the
  // `.wc-manage` block below.
  const [manageOpen, setManageOpen] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [naturalSize, setNaturalSize] = useState({ w: 0, h: 0 });
  const [showConfirm, setShowConfirm] = useState(false);
  // The WIG awaiting delete confirmation, or null — the node, so the dialog can
  // quote its wording without a second lookup that could race the delete.
  const [pendingDelete, setPendingDelete] = useState(null);
  // The band whose add tile was clicked, or null. The canvas only asks; the
  // dialog and its draft live here, so the map never re-renders while someone
  // types.
  const [addForSection, setAddForSection] = useState(null);

  // ⚠️ ONCE PER USER (2026-09-14, revised same day: "only shown one time but
  // everytime for new user"). It briefly opened on every visit; it now follows
  // the same one-time convention as `ebLiveReviewInfoSeen` and `scReviewInfoSeen`.
  //
  // Initialised from storage rather than opened in a mount effect, so a first
  // visit has the dialog in its very first paint. An effect would show the map,
  // then drop a dialog on top a frame later, which reads as a misfire.
  //
  // The flag is per BROWSER, which is what "per user" means in practice here —
  // there is no server-side seen-state to key off, and the same CEO on a second
  // machine will see it once more. That is the right failure direction for an
  // explainer: showing it twice costs a click, never showing it costs the
  // gesture.
  //
  // Writing it here in the initialiser is safe for the reason the celebration
  // banner was NOT (that one consumed its single showing before the render that
  // would have displayed it): this value opens the dialog, so the write and the
  // showing happen together. The button in the toolbar reopens it regardless.
  const [showInfoModal, setShowInfoModal] = useState(() => {
    try {
      if (localStorage.getItem(WIG_CONNECTIONS_INFO_SEEN_KEY)) return false;
      localStorage.setItem(WIG_CONNECTIONS_INFO_SEEN_KEY, "true");
      return true;
    } catch (error) {
      // Storage blocked (private window, blocked cookies). Show the explainer
      // rather than swallow it — this screen's whole gesture is a drag, and
      // never being told about it is the worse outcome.
      return true;
    }
  });

  // ── Zoom that keeps its place ─────────────────────────────────────────────
  // Changing scale alone makes the map appear to lurch: the transform origin
  // is the top-left corner, so whatever the user was looking at slides away
  // and they have to re-find it. This rescales the scroll offsets around the
  // viewport's centre point, so the thing under the middle of the screen stays
  // under the middle of the screen. That, plus the wrapper in the canvas that
  // reserves the scaled footprint, is what makes zooming feel like zooming
  // rather than like the layout breaking.
  //
  // Note the scroll maths happens OUTSIDE the setZoom updater, reading the
  // current zoom from a ref. A state updater must be pure - React may invoke
  // it twice under StrictMode - and scheduling a rAF that writes scroll
  // offsets from inside one would fire the side effect twice, double-applying
  // the correction.
  // ⚠️ A REF TO THE SCROLLER, not `document.querySelector` (2026-09-03). The
  // lookup was `#wig-connections .wcx-scroll`, which works but reaches across
  // the whole document to find a node this component renders — and it is also
  // what made the fit maths hard to reason about, since nothing tied the
  // element being measured to the element being scaled.
  const scrollerRef = useRef(null);
  const zoomRef = useRef(1);

  // ── Fit is a MODE, not a one-off calculation ──────────────────────────────
  // ⚠️ THIS IS THE FIX for "fit is not working properly when Connection
  // Overview is open and close" (2026-09-03).
  //
  // Fit used to read `scroller.clientWidth` once, on the click, and set a
  // scale. That is correct for exactly as long as the scroller stays that
  // width — and the Connection Overview panel is 400-odd pixels of it. So:
  // fit with the panel OPEN, then close it, and the map is left small with a
  // wide empty gutter; fit with it CLOSED, then open it, and the map no longer
  // fits at all. Both were reported, and both are the same bug: a value
  // computed from a width that then changed.
  //
  // So pressing Fit now latches, and any change to the scroller's width
  // re-applies it — panel toggles, window resizes and the split resizer all
  // come through the same ResizeObserver for free.
  const [fitMode, setFitMode] = useState(false);

  const applyZoom = useCallback((next, { keepFit = false } = {}) => {
    // Any manual zoom leaves fit mode. Without this, pressing + after Fit
    // would snap straight back the next time the panel moved, which reads as
    // the control being broken.
    if (!keepFit) setFitMode(false);

    const clamped = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, next));
    const prev = zoomRef.current;
    zoomRef.current = clamped;
    setZoom(clamped);

    const scroller = scrollerRef.current;
    if (!scroller || !prev) return;
    const ratio = clamped / prev;
    const cx = scroller.scrollLeft + scroller.clientWidth / 2;
    const cy = scroller.scrollTop + scroller.clientHeight / 2;
    // Deferred a frame: the wrapper has to grow to its new size before these
    // offsets are reachable, or the browser clamps them to the old scroll
    // range.
    requestAnimationFrame(() => {
      scroller.scrollLeft = cx * ratio - scroller.clientWidth / 2;
      scroller.scrollTop = cy * ratio - scroller.clientHeight / 2;
    });
  }, []);

  // The one place the fit scale is worked out, so the button and the
  // re-fit-on-resize path cannot disagree.
  //
  // `clientWidth` excludes a vertical scrollbar, which matters: a tall map has
  // one, and measuring `offsetWidth` would fit the canvas to a width ~15px
  // wider than it actually has and leave it clipped. `FIT_GUTTER` is the
  // canvas's own right padding plus a little air, so the right-most card is not
  // flush against the edge.
  const computeFitZoom = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller || !naturalSize.w) return null;
    const available = scroller.clientWidth - FIT_GUTTER;
    if (available <= 0) return null;
    return available / naturalSize.w;
  }, [naturalSize.w]);

  const handleFit = useCallback(() => {
    const next = computeFitZoom();
    if (next == null) return;
    setFitMode(true);
    applyZoom(next, { keepFit: true });
  }, [computeFitZoom, applyZoom]);

  // Re-fit whenever the room changes and we are in fit mode. One observer
  // covers the panel opening and closing, the window resizing and the outer
  // split being dragged — none of which fire a React render this component
  // could otherwise hook, because the panel's width is CSS and the resizer
  // lives on a parent screen.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!fitMode || !scroller || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const observer = new ResizeObserver(() => {
      const next = computeFitZoom();
      if (next != null) applyZoom(next, { keepFit: true });
    });
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [fitMode, computeFitZoom, applyZoom]);

  const HeaderMetaData = useContext(HeaderContext);
  useEffect(() => {
    document.title = "Art of Scale | WIG Connections";
    HeaderMetaData.setHeaderMetadata({ headerTitle: "Execution Blueprint" });
    // eslint-disable-next-line
  }, []);

  // ── Breadcrumbs ───────────────────────────────────────────────────────────
  // Built at runtime because the second crumb is the CLIENT's company name —
  // the coach works across many clients, so "Execution Blueprint › Acme › Live
  // Review › WIG Connections" is the trail that actually locates them. The
  // company crumb links back to that client's review screen.
  //
  // ⚠️ The "Live Review" crumb is deliberately INERT (empty url). By the time
  // this screen is up the blueprint is locked, and the Live Review screen's step
  // gate redirects anyone past `finalize_blueprint` straight back here — so a
  // clickable crumb would be a link that visibly does nothing. It stays in the
  // trail because it is what makes this stage legible as part of the live
  // review. Same reasoning as the static array in breadCrumbs.js.
  //
  // If the dashboard hasn't loaded, the company crumb is omitted rather than
  // rendered blank: a shorter honest trail beats a stray empty separator.
  const dispatch = useDispatch();
  const clients = useExecutionBlueprintStoreSelector(selectClients);
  const companyName =
    (clients || []).find((c) => c.blueprintId === blueprintId)?.name || "";
  // ── "Execution Blueprint › ‹Company› WIG Connections" ─────────────────
  // Exactly TWO crumbs, on instruction. The company and the stage are ONE
  // label — "Acme Ltd WIG Connections" — because they name a single thing: this
  // client's connector map. Split into separate crumbs they read as a hierarchy with
  // levels you could climb to, and those levels don't exist: the async review
  // is off-limits once the live review has started, and the live review screen
  // itself bounces anyone whose step has moved on.
  //
  // Only the first crumb navigates (the coach's client dashboard). Falls back to
  // a bare "WIG Connections" before the dashboard lands, rather than a leading space.
  useEffect(() => {
    dispatch(
      setDynamicBreadcrumbs(EXECUTION_BLUEPRINT_WIG_CONNECTIONS, [
        breadCrumbsArray[EXECUTION_BLUEPRINT][0],
        {
          title: companyName
            ? `${companyName} WIG Connections`
            : "WIG Connections",
          url: "",
        },
      ]),
    );
  }, [dispatch, companyName, blueprintId]);

  // ── The graph ─────────────────────────────────────────────────────────────
  // One translation, in one place. `bands`/`nodes` come from the snapshot's
  // sections crossed with the graph's items; `connections` from its edges.
  const {
    bands,
    nodes: serverNodes,
    connections,
    hasGraph,
  } = useMemo(
    () => buildConnectorGraph(snapshot?.connectors, snapshot?.sections),
    [snapshot],
  );

  // ── Reordering a section's WIGs ───────────────────────────────────────────
  // ⚠️ NOW PERSISTED (handoff #25 §3, 2026-09-14). This was a local-only
  // override held on this screen while the backend event was pending. The event
  // exists, so the override is gone — and it had to go rather than stay as a
  // belt-and-braces layer: a local order sitting on top of the server's would
  // silently win over an incoming `section_items_reordered` from the OTHER
  // person in the session, which is the exact disagreement this screen cannot
  // afford. The server's order is the only order now.
  //
  // The canvas already reports the band's full new id list, which is the shape
  // `reorder_section_items` wants, so nothing in it changed for this.
  //
  // `band` is the section key for every WIG band — `buildConnectorGraph` keys
  // them off the snapshot's own `section_key`. The BHAG band is the exception
  // and the canvas refuses to drag it (see `beginPress`), so a band key never
  // reaches the wire that isn't a real section.
  const handleReorderNodes = useCallback((bandKey, orderedIds) => {
    liveReviewSocketStore.getState().reorderSectionItems(bandKey, orderedIds);
  }, []);

  const nodes = serverNodes;

  // ── Adding and removing WIGs from the map (handoff #25 §2) ────────────────
  const handleAddItem = useCallback((sectionKey, draft) => {
    liveReviewSocketStore.getState().addLiveReviewItem(sectionKey, draft);
  }, []);

  // The dialog closes on the echo, not on the click: `addPending` going back to
  // null is the success signal, and closing early would hide a rejection behind
  // a dialog that had already gone.
  const addPendingRef = useRef(false);
  useEffect(() => {
    const busy = addPending != null;
    if (addPendingRef.current && !busy) setAddForSection(null);
    addPendingRef.current = busy;
  }, [addPending]);

  // Opens the confirmation; the delete itself is below. The canvas hands back a
  // node id, so the node is looked up here for the dialog to quote.
  const handleRemoveItem = useCallback(
    (nodeId) => {
      setPendingDelete(nodes.find((node) => node.id === nodeId) || null);
    },
    [nodes],
  );

  const handleConfirmDelete = () => {
    if (!pendingDelete) return;
    liveReviewSocketStore.getState().deleteLiveReviewItem(pendingDelete.id);
    setPendingDelete(null);
  };

  // How many connectors go with it. On THIS screen the number is not a
  // footnote — the whole stage is about these lines, and removing a WIG that
  // sits in the middle of the map takes several of them with it.
  const pendingDeleteConnections = useMemo(() => {
    if (!pendingDelete) return 0;
    return connections.filter(
      (connection) =>
        connection.from === pendingDelete.id ||
        connection.to === pendingDelete.id,
    ).length;
  }, [pendingDelete, connections]);

  const overview = snapshot?.connectors?.overview;
  const step = snapshot?.live_review_step;
  // ── Don't paint this screen until we know it IS this screen ────────────────
  // `live_review_step` decides which live-review stage the user belongs on, and
  // it arrives with the first snapshot — a beat after mount. Rendering this
  // screen's content before then means anyone arriving mid-journey (or reloading
  // this URL after the step moved) sees THIS stage first and is then redirected:
  // two visible screens for one click, the first one wrong.
  //
  // So the skeleton also covers "the step isn't mine yet". The step-routing
  // effect above does the navigating; this only stops the wrong thing painting
  // while it happens. Same guard as the Live Review screen's, and it must stay
  // in step with the other app's copy.
  const stepIsMine = step === LIVE_REVIEW_STEP.CONNECTORS_REVIEW;

  // ── Join, and route on the step ───────────────────────────────────────────
  // This screen connects the socket ITSELF rather than assuming the Live Review
  // screen left the store warm. It has to: §6a.4 says a user who was away when
  // the agent finished gets an email, not a broadcast, and lands here directly —
  // and a plain reload is the same case. `connect` is idempotent.
  useEffect(() => {
    liveReviewSocketStore
      .getState()
      .connect({ uniqueId: blueprintId, viewerRole: PARTICIPANT_ROLE.COACH });
    return () => liveReviewSocketStore.getState().disconnect();
    // eslint-disable-next-line
  }, [blueprintId]);

  // The step routes the screen, both ways. Anyone whose step has gone back to
  // (or never left) `finalize_blueprint` belongs on the Live Review screen;
  // anyone still drafting belongs on the building screen. Checked in an effect
  // and never inline during render — redirecting mid-render is the hazard this
  // feature already recorded once.
  useEffect(() => {
    const target = redirectForStep(step, OWN_STEPS, blueprintId);
    if (target) history.replace(target);
  }, [step, blueprintId, history]);

  // Confirming does NOT navigate. Like the lock before it, the step change is a
  // broadcast to both parties (§6b.2) and the step is what routes the screen —
  // pushing locally here would advance only whoever clicked and strand their
  // counterpart on a connector map that is no longer the live step.
  const canConfirm = joined && step === LIVE_REVIEW_STEP.CONNECTORS_REVIEW;

  const handleConfirmConnections = () => {
    setShowConfirm(false);
    liveReviewSocketStore.getState().confirmConnections();
  };

  // ── Mutations are emits (§6a) ─────────────────────────────────────────────
  // `toEndpoint` is the only sanctioned way to turn a canvas node id into a
  // server endpoint — it is what keeps an item id out of a BHAG endpoint, which
  // the server rejects as `invalid_connector`. Never hand-build the pair.
  //
  // None of these three update local state. The server's echo does, via
  // `connector_added` / `connector_updated` / `connector_deleted`, each carrying
  // a recomputed overview.
  const handleCreate = useCallback((fromId, toId) => {
    liveReviewSocketStore
      .getState()
      .addConnector(toEndpoint(fromId), toEndpoint(toId));
  }, []);

  // A reconnect: the canvas hands back which END moved, so the other end is read
  // off the connection we already hold. `update_connector` is a true in-place
  // edit — the id and `source` survive it — so an AI-suggested line that gets
  // redirected stays marked AI, which is correct: nobody has agreed it yet.
  const handleUpdate = useCallback(
    (connectionId, end, nodeId) => {
      const current = connections.find((c) => c.id === connectionId);
      if (!current) return;
      const fromId = end === "from" ? nodeId : current.from;
      const toId = end === "to" ? nodeId : current.to;
      liveReviewSocketStore
        .getState()
        .updateConnector(connectionId, toEndpoint(fromId), toEndpoint(toId));
    },
    [connections],
  );

  // Soft delete, either party, any connector — AI-suggested or manual. §6a is
  // explicit that there is no "only delete your own" rule, so there is no
  // ownership check to make.
  const handleDelete = useCallback((connectionId) => {
    liveReviewSocketStore.getState().deleteConnector(connectionId);
  }, []);

  const selectedNode = useMemo(
    () => nodes.find((node) => node.id === selectedNodeId) || null,
    [selectedNodeId, nodes],
  );

  // Only computed when something is selected, and only to describe that one
  // node - the panel's job in the design is to explain the selection, not to
  // summarise the whole map twice.
  //
  // ⚠️ KEYED OFF `selectedNode`, NOT `selectedNodeId`. A selection can outlive
  // the node it points at — the WIG is deleted, by this user or by the other
  // party mid-live-review, and the next snapshot simply no longer contains it
  // while `selectedNodeId` sits untouched in local state. Gating on the id let
  // this memo return a summary for a node `selectedNode` had already resolved
  // to null, and the panel's title dereferenced it: "Cannot read properties of
  // null (reading 'label')". Deriving both from the same resolved node is what
  // makes that state unrepresentable rather than merely guarded.
  const selectedSummary = useMemo(() => {
    if (!selectedNode) return null;
    const outgoing = connections.filter((c) => c.from === selectedNodeId);
    const incoming = connections.filter((c) => c.to === selectedNodeId);
    // ⚠️ LABEL ONLY — no code (2026-09-03, on instruction). This read
    // `${node.code} ${node.label}`.
    //
    // Consistent with the cards, which lost their code badge in the same pass,
    // so the panel no longer names a thing the map itself doesn't. The cost is
    // the same one recorded on the card: two WIGs can share a label (there is a
    // "Retention Target" in both the Customer and People bands), and in a flat
    // Drives / Driven-by list there is no band to tell them apart. Falls back to
    // the raw id only when the node is missing from the graph entirely.
    const nameOf = (id) => {
      const node = nodes.find((n) => n.id === id);
      return node ? node.label : id;
    };
    return {
      drives: outgoing.map((c) => nameOf(c.to)),
      dependsOn: incoming.map((c) => nameOf(c.from)),
    };
  }, [selectedNode, selectedNodeId, connections, nodes]);

  return (
    <div>
      <ProjectHeader
        handleNavigation={() =>
          history.push(
            EXECUTION_BLUEPRINT_REVIEW.replace(":blueprintId", blueprintId),
          )
        }
        showBack
      />

      <div id="wig-connections">
        <div className="wc-toolbar">
          <div className="wc-zoom">
            <button
              type="button"
              className="wc-zoom-btn"
              onClick={() => applyZoom(zoom - ZOOM_STEP)}
              disabled={zoom <= ZOOM_MIN}
              aria-label="Zoom out"
            >
              −
            </button>
            {/* A slider rather than only +/− because the design shows a track,
                and because a wide map is faster to frame by dragging than by
                clicking a step at a time. */}
            <input
              type="range"
              className="wc-zoom-range"
              min={ZOOM_MIN * 100}
              max={ZOOM_MAX * 100}
              step={5}
              value={Math.round(zoom * 100)}
              onChange={(event) => applyZoom(Number(event.target.value) / 100)}
              aria-label="Zoom level"
            />
            <button
              type="button"
              className="wc-zoom-btn"
              onClick={() => applyZoom(zoom + ZOOM_STEP)}
              disabled={zoom >= ZOOM_MAX}
              aria-label="Zoom in"
            >
              +
            </button>
            {/* Clickable, because the most common thing to want after zooming
                around is to get back to 100% - and a "Fit" that solves the
                other half: this map is wider than most screens at full size,
                so framing it should be one click rather than a hunt on the
                slider. */}
            <button
              type="button"
              className="wc-zoom-value"
              onClick={() => applyZoom(1)}
              title="Reset to 100%"
            >
              {Math.round(zoom * 100)}%
            </button>
            <button
              type="button"
              className="wc-zoom-fit"
              onClick={handleFit}
              // Latched, so the coach can see fit is still in effect — and that
              // a panel toggle re-fitting is deliberate, not a glitch.
              aria-pressed={fitMode}
            >
              Fit
            </button>
          </div>

          {/* Confirm Connections — BUILT 2026-08-29, when handoff v3's §6b
              shipped `confirm_connections`. The previous note here said §9
              forbade this button; §6b supersedes that outright ("Confirm
              Connections is now built").

              ⚠️ NO completeness gate, and that is the contract's instruction:
              §6b.1 says "don't require any minimum number of connectors before
              enabling the button", and submitting the AI draft with zero manual
              edits is explicitly fine. So this is enabled whenever the step is
              right and we are joined — it is deliberately NOT gated on
              `edges.length`, tempting as that is. The only local guard is
              `advancing`, so it can't be double-pressed. */}
          {/* ⚠️ ONE FLEX CHILD, NOT TWO. `.wc-toolbar` is
              `justify-content: space-between`, so anything added as a third
              child redistributes the free space and slides Confirm Connections
              sideways — which is the exact bug the "Saving…" chip was removed
              for on 2026-09-03 (see the note below). Grouping the explainer
              with the button keeps the toolbar at two children and the primary
              button pinned where it has always been. */}
          <div className="wc-toolbar-right">
            <button
              type="button"
              className="wc-info-btn"
              onClick={() => setShowInfoModal(true)}
            >
              <InfoIcon size={14} />
              <span>How connections work</span>
            </button>

            <button
              type="button"
              className="wc-confirm-btn"
              onClick={() => setShowConfirm(true)}
              disabled={!canConfirm || advancing}
            >
              <CheckIcon size={13} />
              {advancing ? "Confirming…" : "Confirm Connections"}
            </button>
          </div>

          {/* ⚠️ NO "Saving…" CHIP HERE (removed 2026-09-03, on instruction:
              "in wc-toolbar we show saving... text and once it comes button of
              confirm connection moves so please remove it").

              It was a layout bug, not just noise. `.wc-toolbar` is
              `justify-content: space-between`, so the chip was a THIRD flex
              child: every connector edit made it appear, the free space was
              redistributed, and Confirm Connections slid sideways — then slid
              back when the echo landed. Every drag nudged the primary button
              twice.

              Nothing replaces it, deliberately. Connector mutations echo back
              in well under a second, and a second drag is already refused while
              one is in flight by the STORE's own pending guard rather than by
              anything on this screen. So the only state worth reporting is
              failure, which `.wc-banner` below already does. */}
        </div>

        {/* A rejected action. §8's connector codes are verdicts on what the
            user just drew (`invalid_connector`, `duplicate_connector`), so they
            are shown and dismissed rather than retried — the store deliberately
            does not resnapshot on those two. */}
        {lastError && (
          <div className="wc-banner" role="alert">
            <span>{lastError.message}</span>
            <button
              type="button"
              className="wc-banner-btn"
              onClick={() => liveReviewSocketStore.getState().clearError()}
            >
              Dismiss
            </button>
          </div>
        )}

        <div className="wc-body">
          {/* Three distinct states, and the distinction matters:
              - joining with nothing yet  → we have not been told anything
              - joined with `hasGraph`    → a real graph, possibly EMPTY (§6a:
                a twice-failed agent still sends the block with `edges: []`),
                which the canvas renders as a bare map, correctly
              - joined without a graph    → the step says review but no block
                arrived, which is a contract violation rather than an empty map,
                so it says so instead of drawing a blank canvas. */}
          {!hasGraph && lastError ? (
            // ⚠️ Error INSTEAD of the skeleton, on instruction. The banner above
            // carries the message; this replaces the canvas rather than showing
            // a skeleton that implies a map is still on its way.
            <div className="wc-loading" role="alert">
              We couldn&apos;t load the connection map.
            </div>
          ) : !hasGraph || !stepIsMine || joining || !joined ? (
            <WigConnectionsSkeleton />
          ) : !hasGraph ? (
            <div className="wc-loading">
              This step reports no connection map yet. Refreshing usually fixes
              it.
            </div>
          ) : (
            <WigConnectionsCanvas
              nodes={nodes}
              bands={bands}
              connections={connections}
              scale={zoom}
              onCreateConnection={handleCreate}
              onUpdateConnection={handleUpdate}
              onDeleteConnection={handleDelete}
              onSelectionChange={setSelectedNodeId}
              onReorderNodes={handleReorderNodes}
              onAddRequest={setAddForSection}
              onRemoveItem={handleRemoveItem}
              onNaturalSize={setNaturalSize}
              scrollRef={scrollerRef}
            />
          )}

          {panelOpen && (
            <aside className="wc-panel">
              <div className="wc-panel-header">
                <span>Connection Overview</span>
                <button
                  type="button"
                  className="wc-panel-close"
                  onClick={() => setPanelOpen(false)}
                  aria-label="Hide connection overview"
                >
                  <CloseIcon size={14} />
                </button>
              </div>

              <div className="wc-stats">
                {/* Every number here is the SERVER's — §6a says to read the
                    recomputed `overview` off each broadcast rather than deriving
                    or re-fetching. */}
                <OverviewStat
                  value={overview?.total_wigs ?? 0}
                  label="Total WIGs"
                  tone="plain"
                />
                <OverviewStat
                  value={overview?.ai_suggested_count ?? 0}
                  label="AI coach suggestions"
                  tone="violet"
                />
                <OverviewStat
                  value={overview?.total_connections ?? connections.length}
                  label="Connections"
                  tone="teal"
                />
                {/* ⚠️ NOT a fourth "Unreviewed" tile. §6a: `unreviewed_count` is
                    "currently always identical to `ai_suggested_count`" and the
                    handoff says not to build UI logic expecting them to diverge.
                    Two tiles showing the same number every time reads as our
                    arithmetic being broken, so the AI tile above stands for
                    both. The value is still in the payload if a real per-
                    connector review action ever lands. */}
              </div>

              {/* The design shows the idle hint here; with something selected
                  the same slot becomes far more useful as a readout of that
                  node's actual relationships. */}
              {selectedNode && selectedSummary ? (
                <div className="wc-selection">
                  {/* ⚠️ No code prefix (2026-09-03, on instruction). This read
                      `{code} · {label}`; the separator went with the code. */}
                  <p className="wc-selection-title">{selectedNode.label}</p>
                  <div className="wc-selection-group">
                    <span className="wc-selection-label">Contributes to</span>
                    {selectedSummary.drives.length ? (
                      <ul>
                        {selectedSummary.drives.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="wc-selection-empty">
                        Nothing yet — drag from a point to connect one.
                      </p>
                    )}
                  </div>
                  <div className="wc-selection-group">
                    <span className="wc-selection-label">Depends on</span>
                    {selectedSummary.dependsOn.length ? (
                      <ul>
                        {selectedSummary.dependsOn.map((name) => (
                          <li key={name}>{name}</li>
                        ))}
                      </ul>
                    ) : (
                      <p className="wc-selection-empty">Nothing yet.</p>
                    )}
                  </div>
                </div>
              ) : (
                // Leads with what the map already IS, then offers the
                // interaction. The old line ("explore its connections and edit
                // them") cast the reader as the operator of a graph editor,
                // which for a first-time CEO is the highest-risk framing on
                // the highest-risk screen in the flow. The lines are already
                // drawn and AI-suggested; the job here is mostly to agree with
                // them, with the coach driving any edits.
                <div className="wc-panel-hint">
                  <span aria-hidden="true">✦</span>
                  Here&apos;s how your AI coach thinks your WIGs connect. Select any WIG to see its relationships, or adjust them together.
                </div>
              )}

              {/* ⚠️ COLLAPSED BY DEFAULT (2026-09-03). These three rows —
                  Add / Change / Delete — used to occupy permanent space in a
                  panel that already carries stats, a selection readout and a
                  what's-next list. Dragging endpoints between nodes is a
                  learned skill most CEOs in the session will never personally
                  use: the coach drives the edits. So the instructions stay one
                  click away instead of teaching an interaction the reader
                  probably isn't going to perform.

                  The toggle is the heading itself rather than a separate "?"
                  icon: the heading already names what's inside, so making it
                  the control adds no new vocabulary and gives a much larger
                  hit area than a 14px glyph. */}
              <div className="wc-manage">
                <button
                  type="button"
                  className="wc-manage-toggle"
                  onClick={() => setManageOpen((open) => !open)}
                  aria-expanded={manageOpen}
                >
                  <span className="wc-manage-title">
                    How to manage connections
                  </span>
                  <span className="wc-manage-chevron" aria-hidden="true">
                    {manageOpen ? "▾" : "▸"}
                  </span>
                </button>
                {manageOpen &&
                  MANAGE_ROWS.map((row) => (
                    <div className="wc-manage-row" key={row.key}>
                      <span className="wc-manage-glyph" aria-hidden="true">
                        {row.glyph}
                      </span>
                      <div>
                        <p className="wc-manage-row-title">{row.title}</p>
                        <p className="wc-manage-row-text">{row.text}</p>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="wc-next">
                <p className="wc-next-title">What&apos;s next?</p>
                <ol className="wc-next-list">
                  {NEXT_STEPS.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>
            </aside>
          )}

          {!panelOpen && (
            <button
              type="button"
              className="wc-panel-reopen"
              onClick={() => setPanelOpen(true)}
            >
              Connection Overview
            </button>
          )}
        </div>
      </div>

      {/* The design's stage-2 popup — Figma node 33831:49626, built as
          `ConfirmStepModal`'s CONFIRM_WIG_CONNECTIONS preset well before there
          was an action behind it. Its "up next: (3) Year 1 Plan" block is now
          literally true: confirming starts the Year 1 shortlist agent and both
          parties land on the Year 1 screen. Nothing about the preset needed
          changing — this is the moment it was written for. */}
      {showConfirm && (
        <ConfirmStepModal
          {...CONFIRM_WIG_CONNECTIONS}
          confirmLabel="Confirm & Continue"
          loadingLabel="Confirming…"
          confirming={advancing}
          onConfirm={handleConfirmConnections}
          onBack={() => setShowConfirm(false)}
          onClose={() => setShowConfirm(false)}
        />
      )}

      {/* Last, so that if both were somehow open the confirm dialog — the one
          carrying a decision — is the one on top. In practice they can't
          overlap: this opens on arrival and the confirm needs a click. */}
      {showInfoModal && (
        <WigConnectionsInfoModal onClose={() => setShowInfoModal(false)} />
      )}

      {addForSection ? (
        <AddWigDialog
          sectionKey={addForSection}
          sectionLabel={
            bands.find((band) => band.key === addForSection)?.label || ""
          }
          count={nodes.filter((node) => node.band === addForSection).length}
          pending={addPending === addForSection}
          onAdd={handleAddItem}
          onClose={() => setAddForSection(null)}
        />
      ) : null}

      {pendingDelete && (
        <DeleteWigDialog
          item={pendingDelete}
          connectionCount={pendingDeleteConnections}
          onConfirm={handleConfirmDelete}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default WigConnections;
